import {
  FilterName,
  LightName,
  LightPropsDto,
  VaultObjectFilter,
} from "@/types/vault-object-filter-types";
import {
  GAIA_AMBIENT_LIGHT_COLOR,
  GAIA_AMBIENT_LIGHT_INTENSITY,
  GAIA_AMBIENT_LIGHT_INTENSITY_CONSTANT,
  GAIA_DIRECTIONAL_LIGHT_INTENSITY,
  GAIA_DIRECTIONAL_LIGHT_INTENSITY_CONSTANT,
} from "@/utils/gaia-constants";
import HENGE from "@henge-inc/global-types";
import { ToneMappingMode } from "postprocessing";
import * as THREE from "three";

export const LightPresetName = {
  Studio: "Studio",
  Outdoor: "Outdoor",
  Museum: "Museum",
} as const;
export type LightPresetNameType = HENGE.ValuesTypeFromObject<
  typeof LightPresetName
>;

export const VaultObjectThumbnailLights: LightPropsDto[] = [
  {
    name: "thumbnail-directional-light-01",
    type: LightName.DirectionalLight,
    color: GAIA_AMBIENT_LIGHT_COLOR,
    intensity: 0,
    position: new THREE.Vector3(0, 5, 0),
  },
];

const createVaultObjectStudioLights = (
  factor: number = 0.5,
): LightPropsDto[] => [
  {
    name: "default-ambient-light-01",
    type: LightName.AmbientLight,
    color: GAIA_AMBIENT_LIGHT_COLOR,
    intensity:
      GAIA_AMBIENT_LIGHT_INTENSITY_CONSTANT * GAIA_AMBIENT_LIGHT_INTENSITY,
  },
  {
    name: "default-directional-light-01",
    type: LightName.DirectionalLight,
    color: GAIA_AMBIENT_LIGHT_COLOR,
    intensity:
      GAIA_DIRECTIONAL_LIGHT_INTENSITY_CONSTANT *
      GAIA_DIRECTIONAL_LIGHT_INTENSITY *
      factor *
      4,
    position: new THREE.Vector3(0, 5, 0),
  },
];

const createVaultObjectOutdoorLights = (
  factor: number = 0.5,
): LightPropsDto[] => {
  // const getSkyColor = (factor: number) => {
  //   const morning = new THREE.Color("#87CEEB");
  //   const noon = new THREE.Color("#4169E1");
  //   const evening = new THREE.Color("#FF7F50");

  //   if (factor < 0.5) {
  //     return morning.lerp(noon, factor * 2);
  //   } else {
  //     return noon.lerp(evening, (factor - 0.5) * 2);
  //   }
  // };

  const timeOfDay =
    factor < 0.2
      ? "1"
      : factor < 0.4
        ? "2"
        : factor < 0.6
          ? "3"
          : factor < 0.8
            ? "4"
            : "5";

  return [
    {
      name: "outdoor-ambient-light-01",
      type: LightName.AmbientLight,
      color: "#ffffff",
      intensity:
        timeOfDay === "1"
          ? factor * 1
          : timeOfDay === "2"
            ? factor * 2
            : timeOfDay === "3"
              ? factor * 4
              : timeOfDay === "4"
                ? factor * 2
                : timeOfDay === "5"
                  ? factor * 1
                  : factor * 4,
    },
    {
      name: "outdoor-directional-light-01",
      type: LightName.DirectionalLight,
      color: "#ffffff",
      intensity:
        timeOfDay === "1"
          ? factor * 1
          : timeOfDay === "2"
            ? factor * 2
            : timeOfDay === "3"
              ? factor * 4
              : timeOfDay === "4"
                ? factor * 2
                : timeOfDay === "5"
                  ? factor * 1
                  : factor * 4,
      position: new THREE.Vector3(
        Math.cos((factor - 0.5) * Math.PI) * 3,
        5 + Math.sin((factor - 0.5) * Math.PI) * 2,
        0,
      ),
    },
  ];
};

export const MuseumLightFactorSnap = 0.2;
export const MuseumLightFactorDefaultSnap = MuseumLightFactorSnap * 2;

const createVaultObjectMuseumLights = (
  factor: number = MuseumLightFactorDefaultSnap,
): LightPropsDto[] => {
  const getMuseumLightPosition = (factor: number) => {
    const step = Math.round(factor / MuseumLightFactorSnap);
    if (step === 0) {
      // 0
      return new THREE.Vector3(2.5, -0.5, 0); // Right face / 오른쪽 면
    } else if (step === 1) {
      // 1.66
      return new THREE.Vector3(-2.5, -0.5, 0); // Left face / 왼쪽 면
    } else if (step === 2) {
      // 3.332
      return new THREE.Vector3(0, 3, 0); // Top face / 윗면
    } else if (step === 3) {
      // 4.98
      return new THREE.Vector3(0, -3, 0); // Bottom face
    } else if (step === 4) {
      // 6.64
      return new THREE.Vector3(0, -0.5, 2.5); // Front face / 앞면
    } else {
      // 8.3
      return new THREE.Vector3(0, -1, -2); // Back face / 뒷면
    }
  };

  const getMuseumLightAngle = (factor: number) => {
    const step = Math.round(factor / MuseumLightFactorSnap);
    if (step === 0) {
      return (Math.PI / 180) * 30; // Right face / 오른쪽 면
    } else if (step === 1) {
      return (Math.PI / 180) * 30; // Left face / 왼쪽 면
    } else if (step === 2) {
      return (Math.PI / 180) * 30; // Top face / 윗면
    } else if (step === 3) {
      return (Math.PI / 180) * 45; // Bottom face / 아랫면
    } else if (step === 4) {
      return (Math.PI / 180) * 30; // Front face / 앞면
    } else {
      return (Math.PI / 180) * 30; // Back face / 뒷면
    }
  };

  return [
    {
      name: "museum-spot-light-01",
      type: LightName.SpotLight,
      color: "#ffffff",
      intensity: 1,
      position: getMuseumLightPosition(factor),
      angle: getMuseumLightAngle(factor),
      penumbra: 0.5,
      power: 2,
    },
  ];
};

export const VaultObjectFilterLightPresetFunction = {
  [LightPresetName.Studio]: createVaultObjectStudioLights,
  [LightPresetName.Outdoor]: createVaultObjectOutdoorLights,
  [LightPresetName.Museum]: createVaultObjectMuseumLights,
};

const VaultObjectFilterEnvironmentFunction = {
  [LightPresetName.Studio]: (factor: number = 0.8) => ({
    environmentIntensity: factor,
    files: "studio_small_09_1k.hdr",
  }),
  [LightPresetName.Outdoor]: (factor: number = 0.5) => {
    const timeOfDay =
      factor < 0.2
        ? "1"
        : factor < 0.4
          ? "2"
          : factor < 0.6
            ? "3"
            : factor < 0.8
              ? "4"
              : "5";

    return {
      environmentIntensity:
        timeOfDay === "1"
          ? 0.1
          : timeOfDay === "2"
            ? 0.5
            : timeOfDay === "3"
              ? 1
              : timeOfDay === "4"
                ? 0.5
                : 0.1,
      files: `outdoor_${timeOfDay}_1k.jpg`,
    };
  },
  [LightPresetName.Museum]: (factor: number = 0.5) => ({
    environmentIntensity: 0.2,
    files: "studio_small_09_1k.hdr",
  }),
};

export const VaultObjectFilterLightsFunction = {
  [LightPresetName.Studio]: (factor: number = 0.5) => ({
    lights: createVaultObjectStudioLights(factor),
    environment:
      VaultObjectFilterEnvironmentFunction[LightPresetName.Studio](factor),
  }),
  [LightPresetName.Outdoor]: (factor: number = 0.5) => ({
    lights: createVaultObjectOutdoorLights(factor),
    environment:
      VaultObjectFilterEnvironmentFunction[LightPresetName.Outdoor](factor),
  }),
  [LightPresetName.Museum]: (factor: number = 0.5) => ({
    lights: createVaultObjectMuseumLights(factor),
    environment:
      VaultObjectFilterEnvironmentFunction[LightPresetName.Museum](factor),
  }),
};

const Default: VaultObjectFilter = {
  name: FilterName.Default,
  strength: 1,
  background: {
    type: "Color",
    hex: "#ffffff",
  },
  camera: {
    type: "PerspectiveCamera",
    fov: 1,
  },
  lightPreset: {
    name: LightPresetName.Studio,
    factor: 0.5,
    ...VaultObjectFilterLightsFunction[LightPresetName.Studio](),
  },
  postprocessing: {
    effects: {
      ToneMapping: {
        mode: ToneMappingMode.NEUTRAL,
      },
    },
  },
  turntable: {
    speed: 0,
    showHengeTile: false,
  },
};

const Jobs: VaultObjectFilter = {
  name: FilterName.Jobs,
  strength: 1,
  background: {
    type: "Color",
    hex: "#efefef",
  },
  camera: {
    type: "PerspectiveCamera",
    fov: 30,
  },
  lightPreset: {
    name: LightPresetName.Studio,
    factor: 0.5,
    ...VaultObjectFilterLightsFunction[LightPresetName.Studio](),
  },
  postprocessing: {
    effects: {
      ToneMapping: {
        mode: ToneMappingMode.ACES_FILMIC,
      },
      BrightnessContrast: {
        brightness: 0.05,
        contrast: 0.1,
      },
    },
  },
  turntable: {
    speed: 0,
    showHengeTile: false,
  },
};

const Kubrick: VaultObjectFilter = {
  name: FilterName.Kubrick,
  strength: 0.3,
  background: {
    type: "Color",
    hex: "#0a0a0a",
  },
  camera: {
    type: "PerspectiveCamera",
    fov: 1,
  },
  lightPreset: {
    name: LightPresetName.Museum,
    factor: MuseumLightFactorDefaultSnap,
    ...VaultObjectFilterLightsFunction[LightPresetName.Museum](),
  },
  postprocessing: {
    effects: {
      BrightnessContrast: {
        brightness: 0,
        contrast: 0.3,
      },
      Noise: {
        opacity: 0.1,
      },
    },
  },
  turntable: {
    speed: 0,
    showHengeTile: false,
  },
};

const Miyazaki: VaultObjectFilter = {
  name: FilterName.Miyazaki,
  strength: 1,
  background: {
    type: "Color",
    hex: "#C7EBCE",
  },
  camera: {
    type: "PerspectiveCamera",
    fov: 1,
  },
  lightPreset: {
    name: LightPresetName.Outdoor,
    factor: 0.5,
    ...VaultObjectFilterLightsFunction[LightPresetName.Outdoor](),
  },
  postprocessing: {
    effects: {
      ToneMapping: {
        mode: ToneMappingMode.ACES_FILMIC,
      },
      BrightnessContrast: {
        brightness: 0,
        contrast: 0.3,
      },
      LUT: {
        name: "Candy-Cane-01.cube",
      },
      Noise: {
        opacity: 0.1,
      },
    },
  },
  turntable: {
    speed: 0,
    showHengeTile: false,
  },
};

const Nolan: VaultObjectFilter = {
  name: FilterName.Nolan,
  strength: 1,
  background: {
    type: "Color",
    hex: "#ffffff",
  },
  camera: {
    type: "PerspectiveCamera",
    fov: 90,
  },
  lightPreset: {
    name: LightPresetName.Outdoor,
    factor: 0.5,
    ...VaultObjectFilterLightsFunction[LightPresetName.Outdoor](),
  },
  postprocessing: {
    effects: {
      ToneMapping: {
        mode: ToneMappingMode.ACES_FILMIC,
      },
      BrightnessContrast: {
        brightness: 0,
        contrast: 0.3,
      },
      LUT: {
        name: "The-Batman.cube",
      },
    },
  },
  turntable: {
    speed: 0,
    showHengeTile: false,
  },
};

const FilterPreset = {
  [FilterName.Default]: Default,
  [FilterName.Jobs]: Jobs,
  [FilterName.Kubrick]: Kubrick,
  [FilterName.Miyazaki]: Miyazaki,
  [FilterName.Nolan]: Nolan,
} as const;

export default FilterPreset;
